"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { DropdownMenu } from "./DropdownMenu";
import BirthdateGate from "./BirthdateGate";
import { AUTH_CHANGED } from "@/lib/authEvents";
import { useTranslations } from "@/providers/TranslationsProvider";
import { CONFIGS } from "@/constants/configs";
import { useLocalization } from "@/providers/useLocalization";
import { languages, LanguageKey } from "@/providers/LocalizationProvider";
import { ThemeContext } from "@/providers/ThemeContext";
import { useContext } from "react";
import { User } from "@/types/user";
import { keyFor } from "@/lib/translationHelpers";
import { AVATAR_SIZE, isPreSizedAvatar, sizedAvatar } from "@/utils/avatar";
const APPLICATION_TONES: Record<string, string> = {
  draft: "bg-black/[0.06] dark:bg-white/[0.1] text-[var(--foreground-secondary)]",
  pending_review: "bg-[#FF9F0A]/15 text-[#B25E00] dark:text-[#FFB340]",
  approved: "bg-[#34C759]/15 text-[#1F7A36] dark:text-[#4CD964]",
  rejected: "bg-[#FF3B30]/12 text-[#C4281E] dark:text-[#FF6961]"
};
const UserAvatar: React.FC<{
  refetchTrigger: number;
  onSignInClick?: () => void;
}> = ({
  refetchTrigger,
  onSignInClick
}) => {
  const {
    t
  } = useTranslations();
  const {
    language,
    setLanguage,
    config
  } = useLocalization();
  const {
    theme,
    setTheme
  } = useContext(ThemeContext);
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  // Bumped when a sign-in happens elsewhere (e.g. the booking flow's own
  // sign-in modal), so the avatar - and the birth-date prompt - catch up.
  const [signIns, setSignIns] = useState(0);
  useEffect(() => {
    const onSignIn = () => {
      setSignIns(n => n + 1);
      setApplicationStatus(null);
    };
    window.addEventListener(AUTH_CHANGED, onSignIn);
    return () => window.removeEventListener(AUTH_CHANGED, onSignIn);
  }, []);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  // Doctor application status, loaded the first time the menu opens, so an
  // applicant can get back to it ("My application · Under review").
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);
  const isDoctor = !!user?.roles?.some(role => role.toLowerCase() === "trappist");
  useEffect(() => {
    if (!showDropdown || !user || isDoctor || applicationStatus !== null) return;
    let cancelled = false;
    fetch("/api/v1/users/application", {
      cache: "no-store"
    }).then(res => res.ok ? res.json() : null).then((data: {
      return?: {
        status?: string;
      };
    } | null) => {
      if (!cancelled) setApplicationStatus(data?.return?.status ?? "none");
    }).catch(() => {
      if (!cancelled) setApplicationStatus("none");
    });
    return () => {
      cancelled = true;
    };
  }, [showDropdown, user, isDoctor, applicationStatus]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      setShowLangMenu(false);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);
  const getStoredUser = (): User | null => {
    if (typeof window === "undefined") return null;
    try {
      const stored = localStorage.getItem("persist:auth");
      if (!stored) {
        return null;
      }
      const parsed = JSON.parse(stored);
      // redux-persist state is often double-stringified
      const user_0 = typeof parsed.user === 'string' ? JSON.parse(parsed.user) : parsed.user;
      return user_0 || null;
    } catch {
      return null;
    }
  };
  const clearUser = () => {
    setUser(null);
    localStorage.removeItem("auth");
    localStorage.removeItem("persist:auth");
  };
  useEffect(() => {
    // Show the cached user right away, but keep the button disabled
    // (`loading`) until the server confirms who the session belongs to —
    // the cache can be a different account, e.g. after arriving signed in
    // as someone else through /auth/callback.
    const storedUser = getStoredUser();
    if (storedUser) {
      setUser(storedUser);
    }
    setLoading(true);
    const fetchUser = async (retryCount = 0) => {
      const maxRetries = 1;
      try {
        // `full` returns the /users/:uuid record (avatar etc.) in one round trip
        const response = await fetch("/api/v1/auth/me?full=1", {
          credentials: "include",
          cache: "no-store"
        });
        if (!response.ok && response.status === 401 && retryCount < maxRetries) {
          // Attempt refresh
          const refreshResponse = await fetch("/api/v1/auth/refresh", {
            method: "POST",
            credentials: "include"
          });
          if (refreshResponse.ok) {
            // Retry fetchUser info after successful refresh
            return fetchUser(retryCount + 1);
          }
        }
        if (response.status === 401) {
          // No session anymore — don't keep showing the cached account
          clearUser();
        } else if (response.ok) {
          const data_0 = await response.json();
          const finalUser = data_0.user;
          setUser(finalUser);

          // Always overwrite the persisted user so a changed avatar/name doesn't
          // keep flashing the stale copy on the next load
          if (typeof window !== "undefined" && finalUser) {
            let persisted: Record<string, unknown> = {};
            try {
              persisted = JSON.parse(localStorage.getItem("persist:auth") || "{}") || {};
            } catch {
              // corrupt entry - replace it
            }
            localStorage.setItem("persist:auth", JSON.stringify({
              ...persisted,
              user: JSON.stringify(finalUser)
            }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch user:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [refetchTrigger, signIns]);
  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await fetch("/api/v1/auth/logout", {
        method: "POST",
        credentials: "include"
      });
      clearUser();
      setShowDropdown(false);
    } catch (error_0) {
      console.error("Failed to logout:", error_0);
    } finally {
      setLogoutLoading(false);
    }
  };

  // The gate saved a birth date: keep the user (and its cached copy) in step.
  const handleBirthdateSaved = (birthdate: string) => {
    setUser(current => {
      if (!current) return current;
      const next = {
        ...current,
        birthdate
      };
      try {
        const persisted_0 = JSON.parse(localStorage.getItem("persist:auth") || "{}") || {};
        localStorage.setItem("persist:auth", JSON.stringify({
          ...persisted_0,
          user: JSON.stringify(next)
        }));
      } catch {
        // storage unavailable - the next fetch has it anyway
      }
      return next;
    });
  };
  const getInitials = (user_1: User) => {
    if (user_1.firstName && user_1.lastName) {
      return `${user_1.firstName[0]}${user_1.lastName[0]}`.toUpperCase();
    }
    return user_1.username.substring(0, 2).toUpperCase();
  };
  const getDisplayName = (user_2: User) => {
    if (user_2.firstName && user_2.lastName) {
      return `${user_2.firstName} ${user_2.lastName}`;
    }
    return user_2.username;
  };

  // One small variant serves both the 40px button and the 48px menu avatar
  const avatar = user?.avatar ? sizedAvatar(user.avatar, AVATAR_SIZE.small) : null;
  const [activeTheme, setActiveTheme] = useState<"light" | "dark">("light");
  useEffect(() => {
    if (typeof document === "undefined") return;
    const updateActiveTheme = () => {
      if (theme === "system") {
        setActiveTheme(window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
      } else {
        setActiveTheme(theme as "light" | "dark");
      }
    };
    updateActiveTheme();
    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      mq.addEventListener("change", updateActiveTheme);
      return () => mq.removeEventListener("change", updateActiveTheme);
    }
  }, [theme]);
  const toggleTheme = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTheme(activeTheme === "light" ? "dark" : "light");
  };
  return <div className="relative" ref={dropdownRef}>
      {/* Only once the server confirmed the session: a cached user may be stale. */}
      {!loading && user && <BirthdateGate user={user} onSaved={handleBirthdateSaved} onSignOut={handleLogout} />}
      <button onClick={() => {
      if (loading) return;
      setShowDropdown(!showDropdown);
    }} disabled={loading} aria-busy={loading} className={`relative w-10 h-10 rounded-full font-bold flex items-center justify-center transition-all duration-200 ring-2 ring-transparent ${loading ? "bg-black/[0.05] dark:bg-white/[0.1] text-black/[0.2] dark:text-white/[0.2] cursor-wait" : "bg-[var(--surface)] text-[var(--foreground)] cursor-pointer hover:bg-[var(--surface-hover)] active:scale-95 shadow-sm"}`}>
        {loading && user &&
      // Cached user shown dimmed under a progress ring until verified
      <span data-testid="user-avatar-verifying" className="absolute -inset-[3px] rounded-full border-2 border-[var(--color-accent)] border-t-transparent animate-spin pointer-events-none" />}
        {loading && !user ? <div className="w-4 h-4 border-2 border-black/[0.1] dark:border-white/[0.1] border-t-transparent rounded-full animate-spin" /> : user && avatar ? <Image src={avatar} alt={getDisplayName(user)} width={40} height={40} className={`w-full h-full rounded-full object-cover ${loading ? "opacity-50" : ""}`} unoptimized={isPreSizedAvatar(avatar)} /> : user ? <span className={`text-[12px] ${loading ? "opacity-50" : ""}`}>{getInitials(user)}</span> : <div className="w-full h-full rounded-full flex items-center justify-center p-2.5 text-[var(--foreground-secondary)]">
            <svg className="w-full h-full opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>}
      </button>

      {showDropdown && <DropdownMenu isOpen={showDropdown} onClose={() => setShowDropdown(false)} style={{
      minWidth: "260px"
    }}>
          <div className="p-1 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[1.25rem] shadow-xl overflow-hidden">
            {user ? <div className="p-4 bg-[var(--surface)] rounded-xl mb-1">
                <div className="flex items-start gap-3 p-1">
                  <div className="w-12 h-12 rounded-full bg-[var(--card-bg)] flex items-center justify-center text-[var(--foreground)] text-sm font-bold shrink-0 shadow-sm overflow-hidden border border-[var(--card-border)]">
                    {avatar ? <Image src={avatar} alt={getDisplayName(user)} width={48} height={48} className="w-full h-full object-cover" unoptimized={isPreSizedAvatar(avatar)} /> : <span className="text-[12px]">{getInitials(user)}</span>}
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className="font-bold text-[var(--foreground)] truncate text-[14px] leading-tight">
                      {getDisplayName(user)}
                    </p>
                    {user.email && <p className="text-[11px] text-[var(--foreground-secondary)] font-medium mt-1 truncate">
                        {user.email}
                      </p>}
                    <button onClick={() => {
                router.push(CONFIGS.routes.dashboard + '/profile');
                setShowDropdown(false);
              }} className="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-[var(--color-accent)] hover:opacity-80 transition-opacity cursor-pointer uppercase tracking-wider">
                      {t("auth.profile", "User Profile")}
                      <svg className={`w-3 h-3 transition-transform ${config.direction === 'rtl' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    {!isDoctor && <button onClick={() => {
                router.push(`/${language}/join`);
                setShowDropdown(false);
              }} className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-[var(--foreground-secondary)] hover:text-[var(--foreground)] transition-colors cursor-pointer">
                        {applicationStatus && applicationStatus !== "none" ? <>
                            {t("join.my application", "My application")}
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${APPLICATION_TONES[applicationStatus] ?? APPLICATION_TONES.draft}`}>
                              {t(`join.status.${keyFor(applicationStatus)}`, applicationStatus)}
                            </span>
                          </> : t("footer.join", "Join as a doctor")}
                      </button>}
                  </div>
                </div>
              </div> : <div className="p-1">
                <button onClick={() => {
            setShowDropdown(false);
            onSignInClick?.();
          }} className="w-full flex items-center gap-3 px-4 py-3 text-[13px] font-bold text-[var(--foreground)] hover:bg-[var(--surface-hover)] rounded-xl transition-colors duration-150 cursor-pointer">
                  <div className="w-8 h-8 rounded-full bg-[var(--surface)] flex items-center justify-center text-[var(--foreground)]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  <span>{t("auth.sign in", "Sign In")}</span>
                </button>
              </div>}

            <div className="h-px bg-[var(--card-border)] my-1 mx-2" />

            {/* Language Selection */}
            <div className="px-1 py-1">
              <button onClick={() => setShowLangMenu(!showLangMenu)} className="w-full flex items-center justify-between px-4 py-2.5 text-[13px] font-bold text-[var(--foreground)] hover:bg-[var(--surface-hover)] rounded-xl transition-colors duration-150 cursor-pointer" aria-expanded={showLangMenu}>
                <span>{t("labels.language", "Language")}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-[var(--foreground-secondary)]">{languages[language].label}</span>
                  <svg className={`w-3 h-3 transition-transform duration-200 ${showLangMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 9l6 6 6-6" />
                  </svg>
                </div>
              </button>

              {showLangMenu && <div className="mt-0.5">
                  {Object.entries(languages).map(([key, lang]) => {
              const isActive = language === key;
              return <button key={key} onClick={() => {
                setLanguage(key as LanguageKey);
                setShowDropdown(false);
              }} className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] font-bold rounded-xl transition-all duration-150 mb-0.5 last:mb-0 cursor-pointer ${isActive ? "bg-[var(--color-accent)] text-white" : "text-[var(--foreground)] hover:bg-[var(--surface-hover)]"}`}>
                        <span>{lang.label}</span>
                        {isActive && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>}
                      </button>;
            })}
                </div>}
            </div>

            <div className="h-px bg-[var(--card-border)] my-1 mx-2" />

            {/* Appearance */}
            <div className="px-1 py-1">
              <button onClick={e_0 => toggleTheme(e_0)} className="w-full flex items-center justify-between px-4 py-2.5 text-[13px] font-bold text-[var(--foreground)] hover:bg-[var(--surface-hover)] rounded-xl transition-colors duration-150 cursor-pointer">
                <div className="flex items-center gap-3">
                  {activeTheme === 'light' ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                    </svg> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="5" strokeWidth={2.5} />
                      <path strokeLinecap="round" strokeWidth={2.5} d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                    </svg>}
                  <span>{activeTheme === 'light' ? t("labels.darkMode", "Dark Mode") : t("labels.lightMode", "Light Mode")}</span>
                </div>
                <div className={`w-8 h-4 rounded-full p-0.5 transition-colors duration-300 ${activeTheme === 'dark' ? 'bg-[var(--color-accent)]' : 'bg-black/10'}`}>
                  <div className={`w-3 h-3 bg-white rounded-full shadow-sm transition-transform duration-300 ${activeTheme === 'dark' ? config.direction === 'rtl' ? '-translate-x-4' : 'translate-x-4' : 'translate-x-0'}`} />
                </div>
              </button>
            </div>

            {user && <>
                <div className="h-px bg-[var(--card-border)] my-1 mx-2" />
                <div className="px-1 py-1">
                  <button onClick={handleLogout} disabled={logoutLoading} className="w-full flex items-center gap-3 px-4 py-3 text-[13px] font-bold text-[#FF3B30] hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors duration-150 cursor-pointer">
                    {logoutLoading ? <div className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" /> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>}
                    <span>{t("auth.logout", "Logout")}</span>
                  </button>
                </div>
              </>}
          </div>
        </DropdownMenu>}
    </div>;
};
export default UserAvatar;